// =============================================================
// 场景注册中心 (Scene Registry) v2
// 所有场景包在此集中注册。新增场景只需：
//   1. 在 src/templates/<id>/ 下创建 meta.ts + (已有 config.ts + faults.ts + dynamics.ts + knowledge.ts)
//   2. 在此文件 import + 注册一行（SCENES + SCENE_COUPLINGS）
// 其他模块（App / aiStore / aiCoach / LeftSidebar 等）自动识别
// =============================================================
import type { ScenePack, SceneMeta, SceneCoach } from '@/types';
import type { CouplingRule } from '@/engine/dynamics';

// —— FCC 催化裂化 ——
import { fccEquipments, fccPipelines } from './fcc/config';
import { fccFaults } from './fcc/faults';
import { fccMeta } from './fcc/meta';
import { fccCoach } from './fcc/coach';
import { fccCouplings } from './fcc/dynamics';
import { fccKnowledge } from './fcc/knowledge';

// —— 汽车焊装 ——
import { weldingEquipments, weldingPipelines } from './welding/config';
import { weldingFaults } from './welding/faults';
import { weldingMeta } from './welding/meta';
import { weldingCoach } from './welding/coach';
import { weldingCouplings } from './welding/dynamics';
import { weldingKnowledge } from './welding/knowledge';

// —— 数控加工 ——
import { cncEquipments, cncPipelines } from './cnc/config';
import { cncFaults } from './cnc/faults';
import { cncMeta } from './cnc/meta';
import { cncCoach } from './cnc/coach';
import { cncCouplings } from './cnc/dynamics';
// cnc 暂无 knowledge.ts

// —— 注塑成型 ——
import { injectionEquipments, injectionPipelines } from './injection/config';
import { injectionFaults } from './injection/faults';
import { injectionMeta } from './injection/meta';
import { injectionCoach } from './injection/coach';
import { injectionCouplings } from './injection/dynamics';
import { injectionKnowledge } from './injection/knowledge';

// —— 电解铝车间 ——
import { aluminumEquipments, aluminumPipelines } from './aluminum/config';
import { aluminumFaults } from './aluminum/faults';
import { aluminumMeta } from './aluminum/meta';
import { aluminumCoach } from './aluminum/coach';
import { aluminumCouplings } from './aluminum/dynamics';
import { aluminumKnowledge } from './aluminum/knowledge';

// —— 阳极振压成型（电解铝上游）——
import { anodeEquipments, anodePipelines } from './anode/config';
import { anodeFaults } from './anode/faults';
import { anodeMeta } from './anode/meta';
import { anodeCoach } from './anode/coach';
import { anodeCouplings } from './anode/dynamics';
import { anodeKnowledge } from './anode/knowledge';

// —— 阳极焙烧炉（阳极生产二工序）——
import { bakingEquipments, bakingPipelines } from './baking/config';
import { bakingFaults } from './baking/faults';
import { bakingMeta } from './baking/meta';
import { bakingCoach } from './baking/coach';
import { bakingCouplings } from './baking/dynamics';
import { bakingKnowledge } from './baking/knowledge';

// —— 盾构机隧道掘进（首个 3D 场景）——
import { tbmEquipments, tbmPipelines } from './tbm/config';
import { tbmFaults } from './tbm/faults';
import { tbmMeta } from './tbm/meta';
import { tbmCoach } from './tbm/coach';
import { tbmCouplings } from './tbm/dynamics';
import { tbmKnowledge } from './tbm/knowledge';

// —— 海上钻井平台（第二个 3D 场景）——
import { offshoreEquipments, offshorePipelines } from './offshore/config';
import { offshoreFaults } from './offshore/faults';
import { offshoreMeta } from './offshore/meta';
import { offshoreCoach } from './offshore/coach';
import { offshoreCouplings } from './offshore/dynamics';
import { offshoreKnowledge } from './offshore/knowledge';

// —— 高炉炼铁（第三个 3D 场景，专家级工业故障演练）——
import { blastfurnaceEquipments, blastfurnacePipelines } from './blastfurnace/config';
import { blastfurnaceFaults } from './blastfurnace/faults';
import { blastfurnaceMeta } from './blastfurnace/meta';
import { blastfurnaceCoach } from './blastfurnace/coach';
import { blastfurnaceCouplings } from './blastfurnace/dynamics';
import { blastfurnaceKnowledge } from './blastfurnace/knowledge';

// —— 风电机组维护（第四个 3D 场景，清洁能源工业故障演练）——
import { windturbineEquipments, windturbinePipelines } from './windturbine/config';
import { windturbineFaults } from './windturbine/faults';
import { windturbineMeta } from './windturbine/meta';
import { windturbineCoach } from './windturbine/coach';
import { windturbineCouplings } from './windturbine/dynamics';
import { windturbineKnowledge } from './windturbine/knowledge';

// —— 港机码头运维（第五个 3D 场景，港口重载工业故障演练）——
import { portcraneEquipments, portcranePipelines } from './portcrane/config';
import { portcraneFaults } from './portcrane/faults';
import { portcraneMeta } from './portcrane/meta';
import { portcraneCoach } from './portcrane/coach';
import { portcraneCouplings } from './portcrane/dynamics';
import { portcraneKnowledge } from './portcrane/knowledge';

// =============================================================
// 场景注册表（含知识库）
// =============================================================
export const SCENES: Record<string, ScenePack> = {
  fcc: {
    meta: fccMeta,
    coach: fccCoach,
    equipments: fccEquipments,
    pipelines: fccPipelines,
    faults: fccFaults,
    knowledge: fccKnowledge,
  },
  welding: {
    meta: weldingMeta,
    coach: weldingCoach,
    equipments: weldingEquipments,
    pipelines: weldingPipelines,
    faults: weldingFaults,
    knowledge: weldingKnowledge,
  },
  cnc: {
    meta: cncMeta,
    coach: cncCoach,
    equipments: cncEquipments,
    pipelines: cncPipelines,
    faults: cncFaults,
  },
  injection: {
    meta: injectionMeta,
    coach: injectionCoach,
    equipments: injectionEquipments,
    pipelines: injectionPipelines,
    faults: injectionFaults,
    knowledge: injectionKnowledge,
  },
  aluminum: {
    meta: aluminumMeta,
    coach: aluminumCoach,
    equipments: aluminumEquipments,
    pipelines: aluminumPipelines,
    faults: aluminumFaults,
    knowledge: aluminumKnowledge,
  },
  anode: {
    meta: anodeMeta,
    coach: anodeCoach,
    equipments: anodeEquipments,
    pipelines: anodePipelines,
    faults: anodeFaults,
    knowledge: anodeKnowledge,
  },
  baking: {
    meta: bakingMeta,
    coach: bakingCoach,
    equipments: bakingEquipments,
    pipelines: bakingPipelines,
    faults: bakingFaults,
    knowledge: bakingKnowledge,
  },
  tbm: {
    meta: tbmMeta,
    coach: tbmCoach,
    equipments: tbmEquipments,
    pipelines: tbmPipelines,
    faults: tbmFaults,
    knowledge: tbmKnowledge,
  },
  offshore: {
    meta: offshoreMeta,
    coach: offshoreCoach,
    equipments: offshoreEquipments,
    pipelines: offshorePipelines,
    faults: offshoreFaults,
    knowledge: offshoreKnowledge,
  },
  blastfurnace: {
    meta: blastfurnaceMeta,
    coach: blastfurnaceCoach,
    equipments: blastfurnaceEquipments,
    pipelines: blastfurnacePipelines,
    faults: blastfurnaceFaults,
    knowledge: blastfurnaceKnowledge,
  },
  windturbine: {
    meta: windturbineMeta,
    coach: windturbineCoach,
    equipments: windturbineEquipments,
    pipelines: windturbinePipelines,
    faults: windturbineFaults,
    knowledge: windturbineKnowledge,
  },
  portcrane: {
    meta: portcraneMeta,
    coach: portcraneCoach,
    equipments: portcraneEquipments,
    pipelines: portcranePipelines,
    faults: portcraneFaults,
    knowledge: portcraneKnowledge,
  },
};

// =============================================================
// 场景动力学耦合规则注册表
// App.tsx 通过此表获取 DynamicsEngine 所需的 couplings，
// 不再逐个 if-else 判断模板 ID
// =============================================================
export const SCENE_COUPLINGS: Record<string, CouplingRule[]> = {
  fcc: fccCouplings,
  welding: weldingCouplings,
  cnc: cncCouplings,
  injection: injectionCouplings,
  aluminum: aluminumCouplings,
  anode: anodeCouplings,
  baking: bakingCouplings,
  tbm: tbmCouplings,
  offshore: offshoreCouplings,
  blastfurnace: blastfurnaceCouplings,
  windturbine: windturbineCouplings,
  portcrane: portcraneCouplings,
};

/** 已注册的场景 ID 列表（用于自动生成选择器等） */
export const SCENE_IDS = Object.keys(SCENES);

/** 根据 ID 获取场景包 */
export function getScenePack(id: string): ScenePack | undefined {
  return SCENES[id];
}

/** 获取所有可选用的场景 pack 数组（按原始注册顺序） */
export function getAvailableScenes(): ScenePack[] {
  return SCENE_IDS.map((id) => SCENES[id]).filter((s) => s.meta.status === 'available');
}

/** 获取场景元数据 */
export function getSceneMeta(id: string): SceneMeta | undefined {
  return SCENES[id]?.meta;
}

/** 获取场景师傅人设 */
export function getSceneCoach(id: string): SceneCoach | undefined {
  return SCENES[id]?.coach;
}

/** 获取场景动力学耦合规则（供 DynamicsEngine 使用） */
export function getSceneCouplings(id: string): CouplingRule[] | undefined {
  return SCENE_COUPLINGS[id];
}

/** 获取场景知识库（供 aiStore.loadKnowledge 使用） */
export function getSceneKnowledge(id: string): ScenePack['knowledge'] {
  return SCENES[id]?.knowledge;
}
